// FILE: src/pages/info/LegalDocumentPage.jsx
// Privacy policy, Code of Conduct and syllabus as readable pages (public,
// and inside the student area for re-reading after onboarding).
import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import InfoPage from "./InfoPage";
import SimpleText from "../../components/help/SimpleText";
import useAsync from "../../components/help/useAsync";
import { getLegal } from "../../services/courseInfo";
import { ErrorState, LoadingState } from "../../components/ui";

const TITLES = { privacy: "Privacy policy", codeOfConduct: "Code of Conduct", syllabus: "Course syllabus" };
const SLUGS = { privacy: "privacy", codeOfConduct: "code-of-conduct", syllabus: "syllabus" };

export function DocumentBody({ doc, headingLevel = 2 }) {
  return (
    <Stack spacing={3} sx={{ maxWidth: "75ch" }}>
      {(doc.sections || []).map((s, i) => (
        <Box component="section" key={`${s.heading}-${i}`}>
          {s.heading && (
            <Typography component={`h${headingLevel}`} variant="h3" sx={{ mb: 1 }}>
              {s.heading}
            </Typography>
          )}
          <SimpleText text={s.body} headingLevel={Math.min(6, headingLevel + 1)} />
        </Box>
      ))}
    </Stack>
  );
}

export default function LegalDocumentPage({ type, variant = "public" }) {
  const { data, error, loading, reload } = useAsync(() => getLegal(SLUGS[type] || type), [type]);
  const doc = data?.document || data;
  return (
    <InfoPage
      variant={variant}
      title={doc?.title || TITLES[type]}
      description={
        doc?.version
          ? `Version ${doc.version}${doc.effectiveDate ? `, in effect from ${new Date(`${doc.effectiveDate}T12:00:00`).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}` : ""}`
          : undefined
      }
    >
      {loading && !data ? (
        <LoadingState variant="inline" label="Loading…" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : (
        <>
          {doc.summary && <Typography sx={{ fontSize: "1.0625rem", mb: 3, maxWidth: "75ch" }}>{doc.summary}</Typography>}
          <DocumentBody doc={doc} />
        </>
      )}
    </InfoPage>
  );
}
